import React, { useState, useEffect, Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';

import Box from '@mui/material/Box';
import MuiIconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import AddIcon from '@mui/icons-material/Add';

import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import CasesTable from '../cases/CasesTable';
import CasesTableMobile from '../cases/CasesTableMobile';
import CaseModal from '../modals/case';
import RestrictComponent from '../shared/RestrictComponent';
import TrainingModeModal from '../modals/TrainingModeModal';
import CaseDateModal from '../modals/case/CaseDateModal';
import CaseRequestDateModal from '../modals/case/CaseRequestDateModal';
import CaseRequestDateHospitalModal from '../modals/case/CaseRequestDateHospitalModal';
import CaseApproveDateModal from '../modals/case/CaseApproveDateModal';
import CaseRequestConsultationDateModal from '../modals/case/CaseRequestConsultationDateModal';
import CaseApproveConsultationDateModal from '../modals/case/CaseApproveConsultationDateModal';
import Button from '../shared/Button';

import {
  setActiveCase,
  discardActiveCase,
  setActiveTab,
  updateCaseDoc,
  sendChangeDateHospitalEmail,
  requestCaseDateUpdate,
  approveRequestDateUpdate,
  rejectRequestDateUpdate,
  requestConsultationDateUpdate,
  approveRequestConsultationDateUpdate,
  rejectRequestConsultationDateUpdate,
  fetchCases,
} from '../../actions/casesActions';
import { toggleSidebar } from '../../actions/sidebarActions';
import { fetchHospitals } from '../../actions/adminActions';
import { fetchSurgeons } from '../../actions/surgeonsActions';

import { userRoles } from '../../constants';
import { procedureFlows, cmfExtendedProcedureFlows } from '../../constants/cases';

const {
  GLOBAL_ADMIN,
  COUNTRY_ADMIN,
  DISTRIBUTOR_ADMIN,
  SURGEON,
  ENGINEER,
  SALES_REP,
  LOGISTICS,
  PRACTICE_MANAGER,
  MANUFACTURER,
  RADIOLOGY,
} = userRoles;

const getFormattedCaseId = (caseId = '') => {
  const parts = caseId.split('_');
  return parts.length > 1 ? parts[0] : caseId;
};

const getOptimisticCasePatch = (caseItem) => {
  if (!caseItem?.id) {
    return null;
  }

  return {
    id: caseItem.id,
    step: caseItem.step ?? 0,
    status: caseItem.status || '',
    date: caseItem.date || null,
    consultationDate: caseItem.consultationDate || null,
    postponed: !!caseItem.postponed,
    archived: !!caseItem.archived,
    patientLastName: caseItem.patientLastName || caseItem.patientInfo?.lastName || '',
    procedureFlow: caseItem.procedureFlow || '',
    formattedId: caseItem.formattedId || getFormattedCaseId(caseItem.id),
    requestedSurgeryDateNew: caseItem?.requestedSurgeryDate?.newDate || caseItem?.requestedSurgeryDateNew || null,
    requestedConsultationDate: caseItem?.requestedConsultationDate || null,
    requestedConsultationDateNew: caseItem?.requestedConsultationDate?.newDate || caseItem?.requestedConsultationDateNew || null,
  };
};

const DashboardCasesTableContainer = (props) => {
  const {
    cases = [],
    setActiveCase,
    discardActiveCase,
    setActiveTab,
    toggleSidebar,
    withButton,
    trainingMode,
    users,
    surgeons,
    distributors,
    hospitals,
    procedures,
    role,
    userId,
    updateCaseDoc,
    requestCaseDateUpdate,
    approveRequestDateUpdate,
    sendChangeDateHospitalEmail,
    rejectRequestDateUpdate,
    requestConsultationDateUpdate,
    approveRequestConsultationDateUpdate,
    rejectRequestConsultationDateUpdate,
    companies = [],
  } = props;

  const theme = useTheme();

  const dispatch = useDispatch();

  const [isOpen, toggleModal] = useState(false);
  const [trainingConfirmModalOpen, setTrainingConfirmModalState] = useState(false);

  const [selectedCase, setSelectedCase] = useState(null);
  const [loading, setLoading] = useState(false);

  const [dateModalOpen, toggleDateModal] = useState(false);
  const [dateHospitalModalOpen, toggleDateHospitalModal] = useState(false);
  const [requestDateModalOpen, toggleRequestDateModal] = useState(false);
  const [approveDateModalOpen, toggleApproveDateModal] = useState(false);
  const [requestConsultationDateModalOpen, toggleRequestConsultationDateModal] = useState(false);
  const [approveConsultationDateModalOpen, toggleApproveConsultationDateModal] = useState(false);
  const [tableCases, setTableCases] = useState(cases);

  useEffect(() => {
    dispatch(fetchHospitals());
    dispatch(fetchSurgeons());
  }, [dispatch, userId]);

  useEffect(() => {
    setTableCases(cases);
  }, [cases]);

  const applyOptimisticCaseUpdate = (updatedCase) => {
    const optimisticPatch = getOptimisticCasePatch(updatedCase);

    if (!optimisticPatch) {
      return false;
    }

    let hasUpdatedVisibleCase = false;
    const shouldRemove = !!optimisticPatch.postponed || !!optimisticPatch.archived;

    setTableCases((prev) => {
      if (shouldRemove) {
        const filteredCases = prev.filter((caseItem) => {
          if (caseItem.id !== optimisticPatch.id) {
            return true;
          }

          hasUpdatedVisibleCase = true;
          return false;
        });

        return filteredCases;
      }

      return prev.map((caseItem) => {
        if (caseItem.id !== optimisticPatch.id) {
          return caseItem;
        }

        hasUpdatedVisibleCase = true;
        return {
          ...caseItem,
          ...optimisticPatch,
        };
      });
    });

    return hasUpdatedVisibleCase;
  };

  const handleModalClose = async (isFirstStep, selectedFlow, options = {}) => {
    toggleModal(false);

    if (isFirstStep && typeof isFirstStep === "boolean") {
      if ([procedureFlows.CMF, ...cmfExtendedProcedureFlows].includes(selectedFlow)) {
        toggleModal(true);
      } else {
      }
    } else {
      applyOptimisticCaseUpdate(options?.updatedCase);
      discardActiveCase();
    }
  };

  const handleClick = (newCase) => {
    setActiveCase(newCase);
    toggleModal(true);
  };

  const goToFiles = (newCase) => {
    handleClick(newCase);
    setActiveTab(2);
  };

  const handleNewCaseClick = () => {
    if (trainingMode) {
      setTrainingConfirmModalState(true);
    } else {
      toggleModal(true);
    }
  };

  // Request date functionality

  const onSubmitDate = async (date, hospitalId) => {
    try {
      setLoading(true);
      const hospital = hospitals?.find((h) => h.id === hospitalId);
      const doc = {
        date: date?.format(),
        hospital: hospital?.name || '',
        hospitalUniq: { id: hospitalId, name: hospital?.name || '' },
        postponed: false,
      };
      await updateCaseDoc(selectedCase.id, doc);
      applyOptimisticCaseUpdate({
        ...selectedCase,
        ...doc,
        requestedSurgeryDate: null,
        requestedSurgeryDateNew: null,
      });
      sendChangeDateHospitalEmail(selectedCase.id, { ...selectedCase, ...doc }, selectedCase);
      toggleDateModal(false);
      setSelectedCase(null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onSubmitRequestDate = async (date) => {
    try {
      setLoading(true);
      await requestCaseDateUpdate(selectedCase, date);
      applyOptimisticCaseUpdate({
        ...selectedCase,
        requestedSurgeryDate: {
          currentDate: selectedCase?.date || null,
          newDate: date?.format(),
        },
        requestedSurgeryDateNew: date?.format() || null,
      });
      toggleRequestDateModal(false);
      setSelectedCase(null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onSubmitRequestDateHospital = async (date, hospital) => {
    try {
      setLoading(true);
      await requestCaseDateUpdate(selectedCase, date, hospital);
      applyOptimisticCaseUpdate({
        ...selectedCase,
        requestedSurgeryDate: {
          currentDate: selectedCase?.date || null,
          newDate: date?.format(),
          currentHospital: selectedCase?.hospital || '',
          newHospital: hospital,
        },
        requestedSurgeryDateNew: date?.format() || null,
      });
      toggleDateHospitalModal(false);
      setSelectedCase(null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onApproveDate = async () => {
    try {
      if (selectedCase?.requestedSurgeryDate?.newDate || selectedCase?.requestedSurgeryDateNew) {
        setLoading(true);
        await approveRequestDateUpdate(selectedCase);
        const nextHospital = selectedCase?.requestedSurgeryDate?.newHospital || hospitals.find((h) => h.id === selectedCase?.requestedSurgeryDateNewHospital);
        applyOptimisticCaseUpdate({
          ...selectedCase,
          date: selectedCase?.requestedSurgeryDate?.newDate || selectedCase?.requestedSurgeryDateNew || null,
          requestedSurgeryDate: null,
          requestedSurgeryDateNew: null,
          postponed: false,
          hospital: nextHospital?.name || selectedCase?.hospital || '',
          hospitalUniq: nextHospital
            ? {
              id: nextHospital.id || '',
              name: nextHospital.name || '',
            }
            : selectedCase?.hospitalUniq,
        });
        toggleApproveDateModal(false);
        setSelectedCase(null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onRejectDate = async () => {
    try {
      if (selectedCase?.requestedSurgeryDate?.newDate || selectedCase?.requestedSurgeryDateNew) {
        setLoading(true);
        await rejectRequestDateUpdate(selectedCase);
        applyOptimisticCaseUpdate({
          ...selectedCase,
          requestedSurgeryDate: null,
          requestedSurgeryDateNew: null,
        });
        toggleApproveDateModal(false);
        setSelectedCase(null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onAdminApproveDateClick = (row) => {
    handleModalClose();
    setSelectedCase(row);
    toggleApproveDateModal(true);
  };

  const onTBCClick = (row) => {
    handleModalClose();
    const canDirectlyChangeCaseDate = role === userRoles.GLOBAL_ADMIN.name;
    setSelectedCase(row);
    if (canDirectlyChangeCaseDate) {
      toggleDateModal(true);
    } else {
      toggleDateHospitalModal(true);
    }
  };

  const onRequestDateClick = (row) => {
    handleModalClose();
    setSelectedCase(row);
    toggleRequestDateModal(true);
  };

  // Request consultation date functionality

  const onSubmitRequestConsultationDate = async (date) => {
    try {
      setLoading(true);
      await requestConsultationDateUpdate(selectedCase, date);
      applyOptimisticCaseUpdate({
        ...selectedCase,
        requestedConsultationDate: {
          currentDate: selectedCase?.consultationDate || null,
          newDate: date?.format(),
        },
        requestedConsultationDateNew: date?.format() || null,
      });
      toggleRequestConsultationDateModal(false);
      setSelectedCase(null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onApproveConsultationDate = async () => {
    try {
      if (selectedCase?.requestedConsultationDate?.newDate || selectedCase?.requestedConsultationDateNew) {
        setLoading(true);
        await approveRequestConsultationDateUpdate(selectedCase);
        applyOptimisticCaseUpdate({
          ...selectedCase,
          consultationDate: selectedCase?.requestedConsultationDate?.newDate || selectedCase?.requestedConsultationDateNew || null,
          requestedConsultationDate: null,
          requestedConsultationDateNew: null,
        });
        toggleApproveConsultationDateModal(false);
        setSelectedCase(null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onRejectConsultationDate = async () => {
    try {
      if (selectedCase?.requestedConsultationDate?.newDate || selectedCase?.requestedConsultationDateNew) {
        setLoading(true);
        await rejectRequestConsultationDateUpdate(selectedCase);
        applyOptimisticCaseUpdate({
          ...selectedCase,
          requestedConsultationDate: null,
          requestedConsultationDateNew: null,
        });
        toggleApproveConsultationDateModal(false);
        setSelectedCase(null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onAdminApproveConsultationDateClick = (row) => {
    handleModalClose();
    setSelectedCase(row);
    toggleApproveConsultationDateModal(true);
  };

  const onRequestConsultationDateClick = (row) => {
    handleModalClose();
    setSelectedCase(row);
    toggleRequestConsultationDateModal(true);
  };

  const list = useMemo(() => tableCases?.map((caseItem) => {
    let surgeonName = caseItem?.surgeonName || '';

    if (!surgeonName) {
      const surgeonUser = users.find((user) => user.uid === caseItem?.surgeon?.userId);
      surgeonName = surgeonUser
        ? `${surgeonUser?.firstName} ${surgeonUser?.lastName}`
        : "";
    }
    const caseProcedure = procedures.find((procedure) => procedure.id === (caseItem.procedureId || caseItem.procedure));

    return {
      ...caseItem,
      surgeonName,
      procedureName: caseItem?.procedureName || caseProcedure?.name || caseItem?.procedure || 'No procedure',
    };
  }), [tableCases, users, procedures]);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      <Box className="cases-table-container">
        <div className="table-title-container">
          <div className="flex-1">
            <div className="d-flex">
              {withButton && (
                <Box sx={{ display: { sm: 'none' } }} className="m-r-sm">
                  <MuiIconButton color="primary" onClick={toggleSidebar}>
                    <MenuIcon />
                  </MuiIconButton>
                </Box>
              )}

              <Box>
                <Box className="table-title">
                  Upcoming Cases
                  <Box className="badge">{tableCases?.length}</Box>
                </Box>

                {!isMobile && (
                  <Box className='table-subtitle'>
                    Next 30 days
                  </Box>
                )}
              </Box>
            </div>
          </div>

          <div className="d-flex flex-1 flex-end">
            <Box className='d-flex'>
              {trainingMode ? (
                <RestrictComponent
                  roles={[
                    GLOBAL_ADMIN,
                    COUNTRY_ADMIN,
                    DISTRIBUTOR_ADMIN,
                    SURGEON,
                    ENGINEER,
                    SALES_REP,
                    LOGISTICS,
                    PRACTICE_MANAGER,
                    MANUFACTURER,
                  ]}
                >
                  <Button
                    text='Add Case'
                    onClick={() => handleNewCaseClick(true)}
                    icon={<AddIcon color='white' />}
                  />
                </RestrictComponent>
              ) : (
                <RestrictComponent excludeRoles={[MANUFACTURER, LOGISTICS, RADIOLOGY]}>
                  <Button
                    text='Add Case'
                    onClick={() => handleNewCaseClick(true)}
                    icon={<AddIcon color='white' />}
                  />
                </RestrictComponent>
              )}
            </Box>

            {/*<Box sx={{ display: { sm: 'none' } }}>*/}
            {/*  <MuiIconButton color="primary" onClick={() => toggleFilterModal(true)}>*/}
            {/*    <FilterIcon />*/}
            {/*  </MuiIconButton>*/}
            {/*</Box>*/}
          </div>
        </div>
        <Fragment>
          {/* Desktop */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <CasesTable
              cases={list}
              onClick={handleClick}
              goToFiles={goToFiles}
              distributors={distributors}
              procedures={procedures}
              hospitals={hospitals}
              onlyRequired
              loading={loading}
              onAdminApproveDateClick={onAdminApproveDateClick}
              onTBCClick={onTBCClick}
              onRequestDateClick={onRequestDateClick}
              onAdminApproveConsultationDateClick={onAdminApproveConsultationDateClick}
              onRequestConsultationDateClick={onRequestConsultationDateClick}
              companies={companies}
            />

            <LinkContainer to="/cases">
              <div className="primary pointer d-flex p-t-md p-b-md flex-center">
                View All Cases
              </div>
            </LinkContainer>
          </Box>

          {/* Mobile */}
          <Box sx={{ display: { md: 'none' } }}>
            <CasesTableMobile
              cases={list}
              onClick={handleClick}
              withoutPagination={true}
              companies={companies}
            />

            <LinkContainer to="/cases">
              <div className="primary pointer d-flex p-t-md p-b-md flex-center">
                View All Cases
              </div>
            </LinkContainer>
          </Box>
        </Fragment>

        <CaseModal
          open={isOpen}
          onClose={handleModalClose}
          onAdminApproveDateClick={onAdminApproveDateClick}
          onTBCClick={onTBCClick}
          onRequestDateClick={onRequestDateClick}
          onAdminApproveConsultationDateClick={onAdminApproveConsultationDateClick}
          onRequestConsultationDateClick={onRequestConsultationDateClick}
        />

        <TrainingModeModal
          open={trainingConfirmModalOpen}
          onClose={() => setTrainingConfirmModalState(false)}
          onSubmit={() => {
            setTrainingConfirmModalState(false);
            toggleModal(true);
          }}
          title="Training Mode Active"
          text="You are about to create a case for training purposes only. Please cancel and deactivate training if you wish to create an actual case."
          submitText="Proceed"
        />

        <CaseDateModal
          open={dateModalOpen}
          onClose={() => toggleDateModal(false)}
          selectedCase={selectedCase}
          onSubmit={onSubmitDate}
          hospitals={hospitals}
          procedures={procedures}
          loading={loading}
          isAdmin={role === GLOBAL_ADMIN}
        />

        <CaseRequestDateModal
          open={requestDateModalOpen}
          onClose={() => toggleRequestDateModal(false)}
          selectedCase={selectedCase}
          onSubmit={onSubmitRequestDate}
          loading={loading}
        />

        <CaseRequestConsultationDateModal
          open={requestConsultationDateModalOpen}
          onClose={() => toggleRequestConsultationDateModal(false)}
          selectedCase={selectedCase}
          onSubmit={onSubmitRequestConsultationDate}
          loading={loading}
        />

        <CaseRequestDateHospitalModal
          open={dateHospitalModalOpen}
          onClose={() => toggleDateHospitalModal(false)}
          selectedCase={selectedCase}
          onSubmit={onSubmitRequestDateHospital}
          loading={loading}
          hospitals={hospitals}
          surgeons={surgeons}
        />

        <CaseApproveDateModal
          open={approveDateModalOpen}
          onClose={() => toggleApproveDateModal(false)}
          selectedCase={selectedCase}
          onApprove={onApproveDate}
          onReject={onRejectDate}
          hospitals={hospitals}
          loading={loading}
        />

        <CaseApproveConsultationDateModal
          open={approveConsultationDateModalOpen}
          onClose={() => toggleApproveConsultationDateModal(false)}
          selectedCase={selectedCase}
          onApprove={onApproveConsultationDate}
          onReject={onRejectConsultationDate}
          loading={loading}
        />
      </Box>
    </>
  );
};

DashboardCasesTableContainer.propTypes = {
  title: PropTypes.string,
  cases: PropTypes.array,
  withButton: PropTypes.bool,
  setActiveCase: PropTypes.func,
  discardActiveCase: PropTypes.func,
  setActiveTab: PropTypes.func,
  toggleSidebar: PropTypes.func,
};

const mapStateToProps = (state) => {
  const trainingMode = state.sidebar.trainingMode;

  return {
    casesLoaded: state.cases.isLoaded,
    completedLoading: state?.cases?.completedLoading,
    completedLoaded: state?.cases?.completedLoaded,
    archivedLoading: state?.cases?.archivedLoading,
    archivedLoaded: state?.cases?.archivedLoaded,
    trainingMode,
    users: state.users.list,
    surgeons: state.surgeons.list,
    caseId: state.cases?.activeCase?.id,
    countryList: state.countries.list,
    distributors: state.distributors.list,
    teams: state.teams.list,
    hospitals: state.hospitals.list,
    procedures: state.procedures.list,
    role: state.user.currentUser.role,
    userId: state.user.currentUser.uid,
    companies: state.companies?.list || [],
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setActiveCase: (newCase) => dispatch(setActiveCase(newCase)),
    discardActiveCase: () => dispatch(discardActiveCase()),
    setActiveTab: (tab) => dispatch(setActiveTab(tab)),
    toggleSidebar: () => dispatch(toggleSidebar()),
    updateCaseDoc: (caseId, data) => dispatch(updateCaseDoc(caseId, data)),
    sendChangeDateHospitalEmail: (caseId, data, previousData) => dispatch(sendChangeDateHospitalEmail(caseId, data, previousData)),
    requestCaseDateUpdate: (activeCase, date, hospital) => dispatch(requestCaseDateUpdate(activeCase, date, hospital)),
    approveRequestDateUpdate: (activeCase) => dispatch(approveRequestDateUpdate(activeCase)),
    rejectRequestDateUpdate: (activeCase) => dispatch(rejectRequestDateUpdate(activeCase)),
    requestConsultationDateUpdate: (activeCase, date, hospital) => dispatch(requestConsultationDateUpdate(activeCase, date, hospital)),
    approveRequestConsultationDateUpdate: (activeCase) => dispatch(approveRequestConsultationDateUpdate(activeCase)),
    rejectRequestConsultationDateUpdate: (activeCase) => dispatch(rejectRequestConsultationDateUpdate(activeCase)),
    fetchCases: (params) => dispatch(fetchCases(params)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(DashboardCasesTableContainer);
